import {useState} from "react";
import {Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle} from "../../../basic/dialog";
import {Button} from "../../../basic/button";
import {Plus} from "lucide-react";
import {toast} from "sonner";
import {useTranslationWithKeys} from "../../../../hooks/useTranslationWithKeys";
import {useErrorHandler} from "../../../../hooks/useErrorHandler";
import {useGetAdminEntitlementsQuery, type VwpEntitlement} from "../../../../store/generated/api.ts";
import {ProductForm} from "./ProductForm";
import {EntitlementSelector} from "../../../shared/selectors/EntitlementSelector";
import {type ProductData} from "../../../../types/product";

interface CreateProductDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export function CreateProductDialog({open, onOpenChange}: CreateProductDialogProps) {
    const {t} = useTranslationWithKeys();
    const {showErrorToast, showErrorInline} = useErrorHandler();
    const [productData, setProductData] = useState<ProductData>({
        name: "",
        description: "",
        stripeProductId: "",
        softwareDurationDays: undefined
    });
    const [selectedEntitlements, setSelectedEntitlements] = useState<VwpEntitlement[]>([]);

    const {
        data: entitlementsData,
        isLoading: entitlementsLoading,
        error: entitlementsError
    } = useGetAdminEntitlementsQuery();

    const availableEntitlements = entitlementsData?.availableEntitlements || [];

    const handleEntitlementsChange = (entitlements: VwpEntitlement[]) => {
        setSelectedEntitlements(entitlements);
    };

    const handleCreateProduct = () => {
        if (!productData.name.trim()) {
            showErrorToast({message: t('admin.products.create.validation.nameRequired')});
            return;
        }

        if (!productData.stripeProductId.trim()) {
            showErrorToast({message: t('admin.products.create.validation.stripeIdRequired')});
            return;
        }

        if (!productData.description.trim()) {
            showErrorToast({message: t('admin.products.create.validation.descriptionRequired')});
            return;
        }

        toast.success(t('admin.products.create.success', {productName: productData.name}));
        onOpenChange(false);
        setProductData({name: "", description: "", stripeProductId: "", softwareDurationDays: undefined});
        setSelectedEntitlements([]);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="w-[90vw] max-w-7xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>{t('admin.products.create.title')}</DialogTitle>
                    <DialogDescription>
                        {t('admin.products.create.description')}
                    </DialogDescription>
                </DialogHeader>

                <ProductForm
                    productData={productData}
                    onChange={setProductData}
                />

                {entitlementsLoading && (
                    <div className="flex items-center justify-center py-8">
                        <p>{t('common.loading')}</p>
                    </div>
                )}

                {entitlementsError && (
                    <div className="flex items-center justify-center py-8">
                        <div className="text-center">
                            <p className="text-red-500">{showErrorInline(entitlementsError)}</p>
                        </div>
                    </div>
                )}

                {!entitlementsLoading && !entitlementsError && (
                    <EntitlementSelector
                        availableEntitlements={availableEntitlements}
                        selectedEntitlements={selectedEntitlements}
                        onEntitlementsChange={handleEntitlementsChange}
                        loading={entitlementsLoading}
                    />
                )}

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        {t('common.cancel')}
                    </Button>
                    <Button onClick={handleCreateProduct}>
                        <Plus className="h-4 w-4 mr-2"/>
                        {t('admin.products.create.button')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}